export type ValidationRule = (
  | { type: 'required' }
  | { type: 'range'; min?: number; max?: number }
  | { type: 'stringLength'; min?: number; max?: number }
  | { type: 'email' }
  | { type: 'pattern'; pattern: string }
) & { message?: string };

const KNOWN_TYPES = new Set(['required', 'range', 'stringLength', 'email', 'pattern']);

/**
 * Backend sends validationRules as a string that LOOKS like JSON but often
 * isn't valid JSON — e.g. `[{ type: "required" }]` (unquoted key `type`),
 * sometimes with leading whitespace. JSON.parse rejects this outright, which
 * silently dropped ALL rules (including `required`) for any such field.
 *
 * This quotes bare object keys before parsing, so both real JSON and this
 * relaxed JS-object-literal format work. Safe no-op on already-valid JSON.
 */
function toStrictJson(input: string): string {
  // Matches an unquoted key right after `{` or `,` (optionally preceded by
  // whitespace/newlines) and followed by `:`. Avoids touching keys that are
  // already quoted.
  return input.replace(/([{,]\s*)([A-Za-z_$][A-Za-z0-9_$]*)\s*:/g, '$1"$2":');
}

/**
 * Tries strict JSON.parse first, then falls back to the relaxed
 * (unquoted-keys) format. Returns undefined if both attempts fail.
 */
function tryParseJson(input: string): unknown {
  try {
    return JSON.parse(input);
  } catch {
    // fall through to a relaxed attempt below
  }

  try {
    return JSON.parse(toStrictJson(input));
  } catch {
    return undefined;
  }
}

/**
 * Parses the validationRules JSON string from backend field metadata.
 * Handles both backend JSON shapes:
 *   [{ "type": "required" }, { "type": "stringLength", "min": 1, "max": 40 }]
 *   [{ type: "required" }] <- unquoted key, backend's actual format
 *   { "required": true }
 * Returns an empty array on parse failure or missing input.
 */

export function parseValidationRules(validationRulesStr?: string): ValidationRule[] {
  if (!validationRulesStr) return [];
  const trimmed = validationRulesStr.trim();
  if (!trimmed) return [];

  const parsed = tryParseJson(trimmed);
  if (parsed === undefined) return [];

  if (!Array.isArray(parsed)) {
    if ((parsed as { required?: unknown })?.required === true) return [{ type: 'required' }];
    return [];
  }

  return parsed.filter(
    (r: unknown): r is ValidationRule =>
      typeof (r as { type?: unknown })?.type === 'string' &&
      KNOWN_TYPES.has((r as { type: string }).type)
  );
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Base emptiness check shared by all field types: undefined/null/''/'null'/'undefined'.
 * Deliberately does NOT treat 0/'0' as empty here — a plain number field can
 * legitimately have 0 as a real value (e.g. "0 days"). Lookup-specific zero
 * handling is layered on top in isEmptyForField below.
 */
export function isEmptyValue(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  const str = String(value).trim();
  return str === '' || str === 'null' || str === 'undefined';
}

/**
 * Lookup/select editors (selectgrid, selectlookup) store the selected
 * option's id as the field value, and commonly use 0/'0' as their "nothing
 * selected" placeholder — that is NOT a valid choice, so for these editors
 * (and only these) 0/'0' also counts as empty. Plain number fields are
 * intentionally excluded so a real "0" value doesn't fail required checks.
 */
const LOOKUP_EDITOR_TYPES = new Set(['selectgrid', 'selectlookup']);

const validationMessage = (rule: ValidationRule, fallback: string): string =>
  typeof rule.message === 'string' && rule.message.trim() ? rule.message : fallback;

export function isEmptyForField(value: unknown, column?: { editor?: string }): boolean {
  if (isEmptyValue(value)) return true;
  if (column?.editor && LOOKUP_EDITOR_TYPES.has(column.editor)) {
    return value === 0 || value === '0';
  }
  return false;
}

export function validateField(
  rules: ValidationRule[],
  value: unknown,
  fieldLabel: string,
  column?: { editor?: string }
): string | undefined {
  const isEmpty = isEmptyForField(value, column);

  for (const rule of rules) {
    switch (rule.type) {
      case 'required':
        if (isEmpty) return validationMessage(rule, `${fieldLabel} is required`);
        break;

      case 'range': {
        if (isEmpty) break;
        const number = typeof value === 'number' || typeof value === 'string' ? Number(value) : NaN;
        if (!Number.isFinite(number))
          return validationMessage(rule, `${fieldLabel} must be a valid number`);
        if ((rule.min != null && number < rule.min) || (rule.max != null && number > rule.max)) {
          const fallback =
            rule.min != null && rule.max != null
              ? `${fieldLabel} must be between ${rule.min} and ${rule.max}`
              : rule.min != null
                ? `${fieldLabel} must be at least ${rule.min}`
                : `${fieldLabel} must not exceed ${rule.max}`;
          return validationMessage(rule, fallback);
        }
        break;
      }

      case 'stringLength': {
        if (isEmpty) break;
        const str = String(value);
        if (rule.min != null && str.length < rule.min)
          return validationMessage(rule, `${fieldLabel} must be at least ${rule.min} characters`);
        if (rule.max != null && str.length > rule.max)
          return validationMessage(rule, `${fieldLabel} must not exceed ${rule.max} characters`);
        break;
      }

      case 'email': {
        if (isEmpty) break;
        const str = String(value);
        if (!EMAIL_PATTERN.test(str))
          return validationMessage(rule, `${fieldLabel} must be a valid email`);
        break;
      }

      case 'pattern': {
        if (isEmpty) break;
        const str = String(value);
        try {
          if (!new RegExp(rule.pattern).test(str))
            return validationMessage(rule, `${fieldLabel} has an invalid format`);
        } catch {
          // Malformed regex from backend — skip silently
        }
        break;
      }
    }
  }

  return undefined;
}
